import React from 'react';
import Navbar from '../Nav';
import './Experience.css';
import { FiMapPin, FiArrowUpRight } from 'react-icons/fi';

import rbcLogo from '../../assets/rbc_borealis_logo.jpeg';
import unicefLogo from '../../assets/unicef_logo.png';
import uaLogo from '../../assets/ualberta_logo.png';
import melcoLogo from '../../assets/melco_logo.png';
import cognizantLogo from '../../assets/cognizant_logo.png';
import cibcLogo from '../../assets/cibc_logo.png';
import blueprintLogo from '../../assets/blueprint_logo.webp';

const badgeForIndex = (idx) => {
  const palette = ['badge--hot', 'badge--warm', 'badge--mid', 'badge--cool', 'badge--cold', 'badge--coldest'];
  if (idx < palette.length) return palette[idx];
  return palette[palette.length - 1];
};

const timeline = [
  {
    title: 'Graduate Teaching Assistant',
    org: 'University of Alberta',
    logos: [{ src: uaLogo, alt: 'University of Alberta' }],
    location: 'Edmonton, AB',
    dates: 'Sept 2026 – Present',
    bullets: ['Led labs for 100+ students on Python, data structures, and debugging fundamentals.'],
    tags: ['Teaching', 'Python'],
    accent: 'hot',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Research Intern',
    org: 'University of Alberta — Mitsubishi Electric',
    logos: [
      { src: uaLogo, alt: 'University of Alberta' },
      { src: melcoLogo, alt: 'Mitsubishi Electric' }
    ],
    location: 'Edmonton, AB',
    dates: 'May 2026 – Aug 2026',
    bullets: [
      'Rebuilt the reward-shaping pipeline on GPU-accelerated NVIDIA IsaacLab, delivering a 57% improvement in task performance over SOTA LLM-assisted methods',
      'Outperformed state-of-the-art baselines in both sample efficiency and LLM token usage, proving the approach scales without sacrificing speed or cost'
    ],
    tags: ['Reinforcement Learning', 'IsaacLab', 'Gymnasium', 'Bayesian Optimization', 'LLMs'],
    accent: 'hot',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'AI Research Team Member',
    org: 'RBC Let’s Solve It — Borealis AI & UNICEF',
    logos: [
      { src: rbcLogo, alt: 'RBC / Borealis AI' },
      { src: unicefLogo, alt: 'UNICEF' }
    ],
    location: 'Toronto (Remote)',
    dates: 'Oct 2025 – Dec 2025',
    bullets: [
      'Built a RAG pipeline to curate an accurate ReliefWeb-based database to measure impact of natural disasters.',
      'Achieved an accuracy of +75% compared to Natural Language Processing models (spaCy).'
    ],
    tags: ['Applied ML', 'Data Analysis', 'RAG', 'MLOps'],
    accent: 'warm',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Research Intern',
    org: 'University of Alberta — Mitsubishi Electric',
    logos: [
      { src: uaLogo, alt: 'University of Alberta' },
      { src: melcoLogo, alt: 'Mitsubishi Electric' }
    ],
    location: 'Edmonton, AB',
    dates: 'May 2025 – Dec 2025',
    bullets: [
      'Bayesian-optimized rewards and CUDA-parallel PPO for locomotion; 35% metric lift, 80% faster iterations.',
      'Cross-task reward embeddings to improve transfer.'
    ],
    tags: ['Reinforcement Learning', 'IsaacLab', 'Gymnasium', 'Bayesian Optimization', 'LLMs'],
    accent: 'warm',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Teaching Assistant — CMPUT 174/175',
    org: 'University of Alberta',
    logos: [{ src: uaLogo, alt: 'University of Alberta' }],
    location: 'Edmonton, AB',
    dates: 'Jan – Apr (2024, 2025, 2026)',
    bullets: ['Led labs for 100+ students on Python, data structures, and debugging fundamentals.'],
    tags: ['Teaching', 'Python'],
    accent: 'warm',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Research Intern',
    org: 'University of Alberta — Mitsubishi Electric',
    logos: [
      { src: uaLogo, alt: 'University of Alberta' },
      { src: melcoLogo, alt: 'Mitsubishi Electric' }
    ],
    location: 'Edmonton, AB',
    dates: 'May – Aug 2024',
    bullets: [
      'LLM-based reward shaping and fault-adaptive control for robotic reach tasks.',
      'Improved RL efficiency by 10% with robust reward design.'
    ],
    tags: ['LLM shaping', 'Gymnasium'],
    accent: 'cool',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Generative AI Extern',
    org: 'Cognizant',
    logos: [{ src: cognizantLogo, alt: 'Cognizant' }],
    location: 'Remote, US',
    dates: 'Jun – Aug 2024',
    bullets: [
      'Trained CNNs (AlexNet/ResNet/VGG) for image classification; +15% accuracy.',
      'Fine-tuned LLMs for text generation; cut training time by ~10% for demos.'
    ],
    tags: ['GenAI', 'CNNs', 'LLMs'],
    accent: 'cool',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Software Developer Intern',
    org: 'Canadian Imperial Bank of Commerce',
    logos: [{ src: cibcLogo, alt: 'CIBC' }],
    location: 'Toronto, ON',
    dates: 'Sep – Dec 2023',
    bullets: [
      'Built Java Spring API simulators to decouple tests from live DB dependencies.',
      'Automated 150+ SSL tests; improved observability and tracing for services on OpenShift.'
    ],
    tags: ['Spring', 'Docker', 'OpenShift', 'Kubernetes'],
    accent: 'cold',
    link: 'https://github.com/Afraz0Khan'
  },
  {
    title: 'Technical Lead',
    org: 'Blueprint',
    logos: [
      { src: uaLogo, alt: 'University of Alberta' },
      { src: blueprintLogo, alt: 'Blueprint' }
    ],
    location: 'Edmonton, AB',
    dates: 'Nov 2022 – Apr 2023',
    bullets: [
      'Led a student dev team building React.js + Node/Express under a nonprofit initiative; deployed via Netlify + Netlify Functions.',
      'Implemented secure auth with Redux, Firebase Auth, and Firestore; coordinated sprints with code reviews.'
    ],
    tags: ['React', 'Node/Express', 'Redux', 'Firebase'],
    accent: 'coldest',
    link: 'https://github.com/Afraz0Khan'
  }
];

function ExperiencePage() {
  return (
    <div className="experience-page">
      <Navbar />
      <main className="experience-wrapper">
        <header className="experience__header">
          <p className="experience__label">Experience</p>
          <h1 className="experience__title">Where I’ve been building</h1>
        </header>

        <section className="experience-flow">
          {timeline.map((item, idx) => (
            <article key={`${item.title}-${item.dates}`} className="exp-row">
              <div className="exp-logos" aria-hidden="true">
                {(item.logos && item.logos.length ? item.logos : [item.org.slice(0, 2)]).map((logo) =>
                  typeof logo === 'string' ? (
                    <span key={logo} className="exp-logo">
                      {logo}
                    </span>
                  ) : (
                    <span key={logo.alt} className="exp-logo">
                      <img src={logo.src} alt={logo.alt} />
                    </span>
                  )
                )}
              </div>
                <div className="exp-body">
                  <div className="exp-top">
                    <div>
                    <p className={`exp-badge ${badgeForIndex(idx)}`}>
                      {item.dates}
                    </p>
                    <h3>{item.title}</h3>
                    <p className="exp-org">{item.org}</p>
                    <p className="exp-location">
                      <FiMapPin aria-hidden="true" /> {item.location}
                    </p>
                  </div>
                  <a className="exp-link" href={item.link} target="_blank" rel="noreferrer">
                    <FiArrowUpRight aria-hidden="true" />
                  </a>
                </div>
                <ul className="exp-bullets">
                  {item.bullets.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="exp-tags">
                  {item.tags.map((tag) => (
                    <span key={tag} className="chip chip--stack">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}

export default ExperiencePage;
